/**
 * src/components/voice/VoiceToTextModal.tsx
 * --------------------------------------------
 * Voice-to-text capture overlay. Faithful port of the Initiation prototype
 * (VoiceToTextCapture) styling, keeping the real record → transcribe pipeline
 * (MediaRecorder → POST /voice/stt via transcribeAudio).
 */
import { useState, useEffect, useRef } from 'react'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Waveform } from './Waveform'
import { Icon } from '@/components/ui/Icon'
import { startRecording } from '@/lib/audio'
import { transcribeAudio } from '@/api/voice'
import { isRtl as i18nIsRtl } from '@/lib/i18n'
import type { RecordingHandle } from '@/lib/audio'

interface VoiceToTextModalProps {
  isOpen: boolean
  onClose: () => void
  onTranscript: (text: string) => void
}

type Phase = 'recording' | 'transcribing' | 'review' | 'error'

const MAX_SECONDS = 60

export const VoiceToTextModal: FC<VoiceToTextModalProps> = ({ isOpen, onClose, onTranscript }) => {
  const { t, i18n } = useTranslation()
  const rtl = i18nIsRtl(i18n.language || 'en')
  const [phase, setPhase] = useState<Phase>('recording')
  const [transcript, setTranscript] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [secondsLeft, setSecondsLeft] = useState(MAX_SECONDS)

  const recordingRef = useRef<RecordingHandle | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setPhase('recording')
    setTranscript('')
    setErrorMsg('')
    setSecondsLeft(MAX_SECONDS)
    chunksRef.current = []
    void beginRecording()
    return () => stopAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (phase !== 'recording') return
    if (secondsLeft <= 0) void handleStopRecording()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft, phase])

  async function beginRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mr = new MediaRecorder(stream)
      mediaRecorderRef.current = mr
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data) }
      mr.start(250)

      const handle = await startRecording(() => { /* PCM not needed for V2T */ })
      recordingRef.current = handle

      timerRef.current = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000)
    } catch (err) {
      const msg = (err as Error).message || ''
      setErrorMsg(msg && !msg.includes('denied') ? msg : t('v2t.errorMic'))
      setPhase('error')
    }
  }

  function stopAll() {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    recordingRef.current?.stop()
    recordingRef.current = null
    mediaRecorderRef.current?.stop()
    mediaRecorderRef.current?.stream.getTracks().forEach((t) => t.stop())
    mediaRecorderRef.current = null
  }

  async function handleStopRecording() {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    setPhase('transcribing')
    try {
      await new Promise<void>((resolve) => {
        const mr = mediaRecorderRef.current
        if (!mr) { resolve(); return }
        mr.onstop = () => resolve()
        try { mr.stop() } catch { resolve() }
        setTimeout(resolve, 800)
      })
      const blob = new Blob(chunksRef.current, { type: 'audio/webm;codecs=opus' })
      const file = new File([blob], 'recording.webm', { type: blob.type })
      const result = await transcribeAudio(file)
      stopAll()
      setTranscript(result)
      setPhase('review')
    } catch {
      stopAll()
      setErrorMsg(t('v2t.errorTranscribe'))
      setPhase('error')
    }
  }

  const handleConfirm = () => { onTranscript(transcript); onClose() }

  if (!isOpen) return null

  const stateLabel =
    phase === 'recording' ? t('v2t.listening')
      : phase === 'transcribing' ? t('v2t.transcribing')
        : phase === 'review' ? t('v2t.review') : t('v2t.microphone')

  return (
    <div
      dir={rtl ? 'rtl' : 'ltr'}
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,14,20,.6)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 90 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="fade-in"
        style={{ width: 540, maxWidth: 'calc(100vw - 32px)', background: 'var(--bg-elev)', borderRadius: 24, padding: 28, border: '1px solid var(--line)', boxShadow: 'var(--sh-4)', fontFamily: 'var(--font-sans)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 13, flexShrink: 0,
              background: 'var(--grad-helix)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 16px -6px rgba(139,92,246,.55)',
            }}>
              <Icon name="mic" size={18} style={{ color: 'white' }} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-violet)', textTransform: 'uppercase', letterSpacing: '.06em', display: 'flex', alignItems: 'center', gap: 6 }}>
                {t('v2t.input')}
                {phase === 'recording' && (
                  <span style={{ width: 7, height: 7, borderRadius: 999, background: '#E5484D', display: 'inline-block', animation: 'helix-dotPulse 1.4s ease-in-out infinite' }} />
                )}
              </div>
              <div style={{ fontSize: 18, fontWeight: 550, marginTop: 2, color: 'var(--ink)', letterSpacing: '-0.01em' }}>{stateLabel}</div>
            </div>
          </div>
          <button className="btn ghost icon" onClick={onClose} aria-label="Close"><Icon name="close" size={16} /></button>
        </div>

        {phase === 'transcribing' ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '24px 0' }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid var(--brand-violet)', borderTopColor: 'transparent', animation: 'helix-spin 0.8s linear infinite' }} />
            <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>{t('v2t.transcribingBody')}</span>
            <style>{'@keyframes helix-spin{to{transform:rotate(360deg)}}'}</style>
          </div>
        ) : phase === 'error' ? (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <p style={{ fontSize: 13.5, color: 'var(--err)' }}>{errorMsg}</p>
            <button className="btn" style={{ marginTop: 12 }} onClick={onClose}>{t('common.close')}</button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 80, marginBottom: 18 }}>
              <Waveform active={phase === 'recording'} bars={42} />
            </div>

            <div style={{ minHeight: 80, padding: 14, background: 'var(--bg-sunken)', borderRadius: 14, fontSize: 15, lineHeight: 1.55, color: transcript ? 'var(--ink)' : 'var(--ink-4)' }}>
              {phase === 'review' ? (
                <textarea
                  value={transcript}
                  dir="auto"
                  onChange={(e) => setTranscript(e.target.value)}
                  placeholder={t('v2t.placeholder')}
                  style={{ width: '100%', minHeight: 56, border: 0, outline: 'none', background: 'transparent', resize: 'none', fontSize: 15, lineHeight: 1.55, color: 'var(--ink)', fontFamily: 'inherit' }}
                />
              ) : (
                <>{phase === 'recording' ? t('v2t.listeningCount', { s: secondsLeft }) : t('v2t.willAppear')}</>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 18 }}>
              {phase === 'recording' ? (
                <button
                  onClick={() => void handleStopRecording()}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', borderRadius: 999,
                    background: 'linear-gradient(135deg, #E94989, #D6433B)', border: 'none', color: 'white',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    boxShadow: '0 8px 20px -8px rgba(222,60,90,.6)',
                  }}
                >
                  <Icon name="stop" size={15} /> {t('v2t.stopTranscribe')}
                </button>
              ) : <span />}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
                <button className="btn brand" disabled={phase !== 'review' || !transcript.trim()} onClick={handleConfirm}>
                  <Icon name="send" size={14} /> {t('common.send')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
